import {
  DatabaseAuto, IdataArrayResponse,
  IfilterProps, Imainnotification,
  Isuccess, validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { NotificationClient } from '../notif-client';

/**
 * Represents a main notification in the system.
 * This class provides methods to manage notifications including retrieving,
 * filtering, and marking them as read.
 *
 * @class NotificationMain
 * @extends DatabaseAuto
 * @example
 * // Get user notifications
 * const { count, dataArr } = await NotificationMain.getAll(0, 10);
 *
 * // Mark a notification as viewed
 * await notificationInstance.updateViewed();
 */
export class NotificationMain extends DatabaseAuto {
  /** Base URL for notification endpoints */
  static readonly baseUrl = '/notification';

  /** ID of the user who can access this notification */
  readonly authUser: Imainnotification['authUser'];

  /** ID of the user who will receive this notification */
  readonly targetId: Imainnotification['targetId'];

  /** Type of the target ID (e.g., 'user', 'company') */
  readonly targetType: Imainnotification['targetType'];

  /** Title of the notification */
  readonly title: Imainnotification['title'];

  /** Main content/body of the notification */
  readonly body: Imainnotification['body'];

  /** Optional icon URL or identifier for the notification */
  readonly icon?: Imainnotification['icon'];

  /** Type/category of the notification */
  readonly notifType?: Imainnotification['notifType'];

  /** ID of the entity that triggered this notification */
  readonly notifInvokerId?: Imainnotification['notifInvokerId'];

  /** Expiration date for the notification */
  readonly expireOn?: Imainnotification['expireOn'];

  /** Whether the notification has been viewed */
  readonly viewed: Imainnotification['viewed'];

  /** Username of the notification recipient */
  readonly username: Imainnotification['username'];

  /** Email of the notification recipient */
  readonly email?: Imainnotification['email'];

  /** Phone number of the notification recipient */
  readonly phone?: Imainnotification['phone'];

  /** Country code for the phone number */
  readonly phoneCode?: Imainnotification['phoneCode'];

  /** Additional metadata for the notification */
  readonly extraDetails?: Imainnotification['extraDetails'];

  /**
   * Creates a new NotificationMain instance with validated data
   * @param {Imainnotification} data - The notification data
   * @throws {Error} If the notification data is invalid
   */
  constructor(data: Imainnotification) {
    validateData(data, {
      title: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Title must be a non-empty string'
      },
      body: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Body must be a non-empty string'
      },
      icon: {
        required: false,
        validate: (v) =>
          v === undefined ||
          typeof v === 'string',
        message: 'Icon must be a string if provided'
      },
      notifType: {
        required: false,
        validate: (v) =>
          v === undefined ||
          typeof v === 'string',
        message: 'Notification type must be a string if provided'
      },
      notifInvokerId: {
        required: false,
        validate: (v) =>
          v === undefined ||
          (typeof v === 'string' && v.trim().length > 0),
        message: 'Notification invoker ID must be a non-empty string if provided'
      },
      expireOn: {
        required: false,
        validate: (v) => {
          // If expireOn is provided, validate it's a valid date string
          if (v !== undefined) {
            const expireDate = new Date(v);

            return !isNaN(expireDate.getTime()) && expireDate > new Date();
          }

          return true;
        },
        message: 'Expiration date must be a valid future date if provided'
      }
    });

    super(data);
    this.authUser = data.authUser;
    this.targetId = data.targetId;
    this.targetType = data.targetType;
    this.title = data.title;
    this.body = data.body;
    this.icon = data.icon;
    this.notifType = data.notifType;
    this.notifInvokerId = data.notifInvokerId;
    this.viewed = data.viewed;
    this.username = data.username;
    this.email = data.email;
    this.phone = data.phone;
    this.phoneCode = data.phoneCode;
    this.extraDetails = data.extraDetails;
  }

  /**
   * Retrieves a paginated list of notifications
   * @param {number} [offset=0] - The number of items to skip before starting to collect the result set
   * @param {number} [limit=20] - The maximum number of items to return
   * @param {string} [targetId] - The ID of the target to filter notifications for
   * @returns {Promise<{count: number, dataArr: NotificationMain[]}>} Object containing count and array of notifications
   * @throws {Error} If the API request fails
   * @example
   * // Get first 20 notifications
   * const { count, dataArr } = await NotificationMain.getAll();
   */
  static async getAll(offset = 0, limit = 20, targetId?: string) {
    let query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();

    if (targetId) {
      query += `&targetId=${targetId}`;
    }
    const observer$ = NotificationClient
      .ehttp.makeGet<IdataArrayResponse<Imainnotification>>(`${NotificationMain.baseUrl}?${query}`);
    const notifications = await lastValueFrom(observer$);

    return {
      count: notifications.count,
      dataArr: notifications.data.map(val => new NotificationMain(val))
    };
  }

  /**
   * Retrieves notifications based on filter criteria
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{count: number, dataArr: NotificationMain[]}>} Object containing count and filtered notifications
   * @throws {Error} If the API request fails
   * @example
   * // Get unread notifications
   * const { dataArr } = await NotificationMain.filterAll({
   *   conditions: { viewed: false }
   * });
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = NotificationClient.ehttp
      .makePost<IdataArrayResponse<Imainnotification>>(
        `${NotificationMain.baseUrl}/filter`,
        filter
      );
    const notifns = await lastValueFrom(observer$);

    return {
      count: notifns.count,
      dataArr: notifns.data.map((val) => new NotificationMain(val))
    };
  }

  /**
   * Marks all notifications as read for the current user
   * @param {string} [targetId] - The ID of the target to mark notifications for
   * @returns {Promise<Isuccess>} Success status of the operation
   * @throws {Error} If the API request fails
   * @example
   * await NotificationMain.markAllAsRead();
   */
  static markAllAsRead(targetId?: string): Promise<Isuccess> {
    const observer$ = NotificationClient.ehttp
      .makePost<Isuccess>(`${NotificationMain.baseUrl}/mark-all-read`, { targetId });

    return lastValueFrom(observer$);
  }

  /**
   * Gets the count of unviewed notifications for the current user
   * @param {string} [targetId] - The ID of the target to get unviewed notifications for
   * @returns {Promise<number>} Count of unviewed notifications
   * @throws {Error} If the API request fails
   * @example
   * const unreadCount = await NotificationMain.getUnviewedLength();
   */
  static getUnviewedLength(targetId?: string): Promise<number> {
    const observer$ = NotificationClient.ehttp
      .makePost<number>(`${NotificationMain.baseUrl}/unviewed-length`, { targetId });

    return lastValueFrom(observer$);
  }

  /**
   * Marks the current notification as viewed
   * @returns {Promise<NotificationMain>} The updated notification instance
   * @throws {Error} If the update fails or notification doesn't exist
   * @example
   * const updatedNotification = await notificationInstance.updateViewed();
   */
  async updateViewed(): Promise<NotificationMain> {
    const observer$ = NotificationClient.ehttp
      .makePost<Imainnotification>(
        `${NotificationMain.baseUrl}/update-viewed`,
        { _id: this._id }
      );

    return new NotificationMain(await lastValueFrom(observer$));
  }

  /**
   * Checks if the notification has expired
   * @returns {boolean} True if the notification has expired, false otherwise
   */
  isExpired(): boolean {
    return this.expireOn ? new Date(this.expireOn) < new Date() : false;
  }
}
